import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import type { FallbackProps } from 'react-error-boundary'

import '@/services/firebase'
// Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css'

// Tailwind css
import './styles/globals.css'
import './tailwind.css'

import './styles/demoComponents.css'
import './styles/companyThemes.css'

// i18n (needs to be bundled)
import './i18n'

import * as Sentry from '@sentry/react'
import { defaultShouldDehydrateQuery, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { NuqsAdapter } from 'nuqs/adapters/react'
// Tagmanager
import TagManager from 'react-gtm-module'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { publicConfig } from '@/config/publicConfig'
// Redux
import { useBreakpoints } from '@/hooks/useBreakpoints'
import { ConnectionHealthProvider } from '@/providers/ConnectionHealthProvider'
// Router
import { routeTree } from '@/router/routeTree'
import { persister, queryClient } from '@/services/api'
import { persistor, store } from '@/store'
import { reactGA } from '@/utils/analytics/reactGA'
import { FirebaseAuthProvider } from '@/utils/firebaseAuthProvider'
import {
  isPromoEntryPath,
  isSensitivePromoDocument,
  markSensitivePromoDocument,
  protectPromoEntry,
} from '@/utils/promoEntry'
import { shouldReportSentryEvent } from '@/utils/sentryErrorFilters'
import { applyThemePreferenceToDom, readStoredThemePreference } from '@/utils/theme'

protectPromoEntry()

const GA_MEASUREMENT_ID = 'G-QMKPXQB1WE'

const suppressSensitiveTelemetry = () => {
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true
  ;(window as unknown as Record<string, unknown>).gtag = () => undefined
  void Sentry.getReplay()?.stop({ flush: false })
  void Sentry.close(0)
}

if (isSensitivePromoDocument()) suppressSensitiveTelemetry()

const tagManagerArgs = {
  gtmId: GA_MEASUREMENT_ID,
}

const tracePropagationTargets = [
  'localhost',
  window.location.origin,
  publicConfig.api.baseUrl,
].filter((target): target is string => Boolean(target))

if (!isSensitivePromoDocument()) {
  reactGA.initialize(GA_MEASUREMENT_ID)
  TagManager.initialize(tagManagerArgs)

  Sentry.init({
    dsn: publicConfig.sentry.dsn || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
      Sentry.replayIntegration({
        networkCaptureBodies: false,
        beforeAddRecordingEvent: (event) => (isSensitivePromoDocument() ? null : event),
      }),
    ],
    // Preserve the restrictive collection settings from Sentry 10.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
    },
    beforeSendLog: (log) => (isSensitivePromoDocument() ? null : log),
    beforeBreadcrumb: (breadcrumb) => (isSensitivePromoDocument() ? null : breadcrumb),
    beforeSendTransaction: (event) => (isSensitivePromoDocument() ? null : event),
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets,
    // Session Replay
    initialScope: {
      extra: {
        env_runtime: import.meta.env.MODE,
        env: publicConfig.app.environment,
        api: publicConfig.api.baseUrl,
      },
    },
    environment: publicConfig.app.environment,
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event) {
      return !isSensitivePromoDocument() && shouldReportSentryEvent(event) ? event : null
    },
  })
}
const container = document.getElementById('root') as HTMLDivElement
const root = createRoot(container)

// Prevent first-paint theme mismatch before React bootstraps.
applyThemePreferenceToDom(readStoredThemePreference('theme'))

document.addEventListener('DOMContentLoaded', () => {
  const metaTag = document.querySelector('meta[name="url"]')
  const metaTagOg = document.querySelector('meta[name="og:url"]')
  if (metaTag) {
    metaTag.setAttribute('content', window.location.origin)
  }

  if (metaTagOg) {
    metaTagOg.setAttribute('content', window.location.origin)
  }
})

// Create TanStack Router instance
const router = createRouter({ routeTree })
router.subscribe('onBeforeNavigate', ({ toLocation }) => {
  if (!isPromoEntryPath(toLocation.pathname)) return
  markSensitivePromoDocument()
  suppressSensitiveTelemetry()
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const ResponsiveQueryDevtools = () => {
  const { isMobile } = useBreakpoints()
  return isMobile ? null : <ReactQueryDevtools initialIsOpen={false} />
}

export const ErrorFallBack = ({ error, resetErrorBoundary }: FallbackProps) => {
  const errorMessage =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error'

  if (!isSensitivePromoDocument())
    Sentry.captureMessage('ErrorBoundary - Crash Default', {
      extra: {
        defaultLayout: true,
        location: location.pathname,
        error: errorMessage,
      },
    })
  return (
    <div role="alert">
      <p className="text-xl text-white">Something went wrong, contact Frontend Team</p>
      <p className="text-xl text-red-500">Let them know about the following error:</p>
      <pre className="my-8">{errorMessage}</pre>
      <button
        onClick={resetErrorBoundary}
        className={
          'm-8 flex gap-2 px-2 py-1 mx-0.5 bg-red-400 text-black border-b border-transparent hover:bg-dark hover:text-white hover:rounded-t'
        }
      >
        Click here to try again and reload website
      </button>
    </div>
  )
}

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <FirebaseAuthProvider>
        <ConnectionHealthProvider>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              buster: '2025-08-11-1',
              dehydrateOptions: {
                shouldDehydrateQuery: (query) =>
                  query.queryKey[0] !== 'affiliates' &&
                  query.queryKey[0] !== 'qrPromo' &&
                  defaultShouldDehydrateQuery(query),
              },
            }}
          >
            <Suspense>
              <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                  <NuqsAdapter>
                    <RouterProvider router={router} />
                  </NuqsAdapter>
                </PersistGate>
              </Provider>
            </Suspense>
            {import.meta.env.DEV && !(window as any).ReactNativeWebView && (
              <ResponsiveQueryDevtools />
            )}
          </PersistQueryClientProvider>
        </ConnectionHealthProvider>
      </FirebaseAuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
